import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import {
  TEAM_REPORTING_API_SORT_DIRECTION,
  TEAM_REPORTING_AREA_DEFAULT_SORT,
  TEAM_REPORTING_AREA_LOAD_VALUE,
  TEAM_REPORTING_AREA_NAME_SORT,
  TEAM_REPORTING_DEFAULT_OPTION,
  TEAM_REPORTING_DEFAULT_PAGE,
  TEAM_REPORTING_DEFAULT_PAGE_SIZE,
  TEAM_REPORTING_EMPTY_META,
  TEAM_REPORTING_ERROR_MESSAGES,
  TEAM_REPORTING_REGION_LOAD_VALUE,
} from "../../constants/teamReportingConstant";
import type {
  FetchAreaOptionsArgs,
  FetchTeamsReportingLeadsDropdownArgs,
  FetchTeamsReportingRatingRolesArgs,
  ResetDependentDropdownsPayload,
  TeamReportingApiRow,
  TeamReportingLeadsPayload,
  TeamReportingOption,
  TeamReportingRoleMember,
  TeamReportingRatingRolesPayload,
  TeamReportingSearchMeta,
  TeamReportingSearchPayload,
  TeamReportingSearchRequest,
  TeamReportingSearchResult,
  TeamReportingSortDirection,
  TeamReportingTableRow,
  TeamsReportSelectorState,
  TeamsReportState,
} from "../../types/teamReporting.types";
import { fetchLocations } from "../../services/auth/ubtService";
import { APIResponse } from "../../types/selectUBT.types";
import {
  addDefaultOption,
  transformToDropdownOptions,
} from "../../utils/dropdownTransformers";
import { formatTimestampMDY } from "../../utils/date";
import { printHtmlReportToPdf } from "../../utility/PdfReportUtility";
import { getPstDateTimeSuffix } from "../../utils/pstDateTimeSuffix";
import {
  downloadCsvFile,
  resolveCsvFileName,
} from "../../utility/CsvReportUtility";

type NormalizableRecord = Record<string, unknown>;

type StringifiableValue = string | number | boolean;

const initialState: TeamsReportState = {
  regionOptions: [],
  areaOptions: [],
  ratingOptions: [],
  roleOptions: [],
  leadsDropdownOptions: [],
  teamsSearchResults: [],
  teamsSearchMeta: { ...TEAM_REPORTING_EMPTY_META },
  raw: null,
  loading: false,
  error: null,
  leadsLoading: false,
  leadsError: null,
  loadingRegion: false,
  loadingArea: false,
  teamsSearchLoading: false,
  teamsSearchError: null,
  pdfDownloadLoading: false,
  pdfDownloadError: null,
  csvDownloadLoading: false,
  csvDownloadError: null,
  exportUBTDetailsLoading: false,
  exportUBTDetailsError: null,
  membershipCountLoading: false,
  membershipCountError: null,
};

const isRecord = (value: unknown): value is NormalizableRecord =>
  typeof value === "object" && value !== null;

const getNestedRecord = (
  value: unknown,
  key: string,
): NormalizableRecord | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const nestedValue = value[key];
  return isRecord(nestedValue) ? nestedValue : undefined;
};

const getArrayValue = (value: unknown, key: string): unknown[] => {
  if (!isRecord(value)) {
    return [];
  }

  const candidate = value[key];
  return Array.isArray(candidate) ? candidate : [];
};

const pickFirstArray = (candidates: unknown[][]): unknown[] =>
  candidates.find((candidate) => candidate.length > 0) ?? [];

const isStringifiableValue = (value: unknown): value is StringifiableValue =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const isOptionId = (value: unknown): value is string | number =>
  typeof value === "string" || typeof value === "number";

const toDisplayString = (value: unknown): string =>
  isStringifiableValue(value) ? String(value).trim() : "";

const extractErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string => {
  if (!isRecord(error)) {
    return fallbackMessage;
  }

  const response = getNestedRecord(error, "response");
  const data = response ? getNestedRecord(response, "data") : undefined;

  const message =
    data?.message ??
    data?.error ??
    data?.detail ??
    response?.statusText ??
    error.message;

  return isStringifiableValue(message) ? String(message) : fallbackMessage;
};

/**
 * Attempts to parse a JSON error message from a Blob-like value.
 * Returns the parsed message or undefined if parsing fails.
 */
const parseBlobErrorMessage = async (
  rawData: unknown,
): Promise<string | undefined> => {
  if (
    typeof rawData !== "object" ||
    rawData === null ||
    typeof (rawData as Blob).text !== "function"
  ) {
    return undefined;
  }
  try {
    const text = await (rawData as Blob).text();
    const parsed: unknown = JSON.parse(text);
    if (!isRecord(parsed)) return undefined;
    const msg = parsed["message"] ?? parsed["error"] ?? parsed["detail"];
    return isStringifiableValue(msg) ? String(msg) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Async variant of extractErrorMessage that handles blob error responses.
 * When responseType is "blob", Axios delivers the error body as a Blob even
 * for non-2xx responses. This function reads the blob text, parses JSON,
 * and extracts the error message before falling back to the sync helper.
 */
const extractBlobErrorMessage = async (
  error: unknown,
  fallbackMessage: string,
): Promise<string> => {
  if (isRecord(error)) {
    const response = getNestedRecord(error, "response");
    const rawData = response ? response["data"] : undefined;
    const blobMessage = await parseBlobErrorMessage(rawData);
    if (blobMessage) return blobMessage;
  }
  return extractErrorMessage(error, fallbackMessage);
};

const getFirstDefinedValue = (
  record: NormalizableRecord,
  keys: readonly string[],
): unknown => {
  for (const key of keys) {
    const value = record[key];
    if (isStringifiableValue(value) && String(value).trim() !== "") {
      return value;
    }
  }

  return undefined;
};

const formatTeamRoleName = (role: TeamReportingRoleMember): string => {
  const first = toDisplayString(role.firstName);
  const last = toDisplayString(role.lastName);

  return [first, last].filter(Boolean).join(" ");
};

const pickRoleNames = (
  teamRoles: TeamReportingRoleMember[] | undefined,
  roleName: string,
): string => {
  const normalizedTarget = roleName.toLowerCase().replaceAll(/[^a-z0-9]/g, "");
  const safeTeamRoles = teamRoles ?? [];

  return safeTeamRoles
    .filter(
      (role) =>
        toDisplayString(role.teamRole)
          .toLowerCase()
          .replaceAll(/[^a-z0-9]/g, "") === normalizedTarget,
    )
    .map(formatTeamRoleName)
    .filter(Boolean)
    .join(", ");
};

const normalizeTeamsSearchItems = (
  items: TeamReportingApiRow[],
): TeamReportingSearchResult[] =>
  items.map((row) => ({
    ...row,
    teamId: toDisplayString(row.ubtId ?? row.teamId),
    ubtName: toDisplayString(row.ubtName ?? row.teamName),
    teamLevelRating: toDisplayString(
      row.teamLevelRating ??
        row.likertScaleRating ??
        row.likertRatingDescription,
    ),
    regionName: toDisplayString(row.regionName ?? row.regionDescription),
    area: toDisplayString(row.area ?? row.areaDescription),
    consultant:
      toDisplayString(row.consultant) ||
      pickRoleNames(row.teamRoles, "Consultant"),
    sponsor:
      toDisplayString(row.sponsor) || pickRoleNames(row.teamRoles, "Sponsor"),
    laborSponsor:
      toDisplayString(row.laborSponsor) ||
      pickRoleNames(row.teamRoles, "Labor Sponsor"),
    proxy: toDisplayString(row.proxy) || pickRoleNames(row.teamRoles, "Proxy"),
    thWpsChampion:
      toDisplayString(row.thWpsChampion) ||
      pickRoleNames(row.teamRoles, "TH/WPS Champion"),
    staffingChampion:
      toDisplayString(row.staffingChampion) ||
      pickRoleNames(row.teamRoles, "Staffing Champion"),
    coLead:
      toDisplayString(row.coLead) || pickRoleNames(row.teamRoles, "Co-Lead"),
    laborCoLead:
      toDisplayString(row.laborCoLead) ||
      pickRoleNames(row.teamRoles, "Labor Co-Lead"),
    lastUpdatedDate: formatTimestampMDY(
      row.lastUpdatedDate ?? row.teamDateLastUpdated,
    ),
  }));

const uniqById = (items: TeamReportingOption[]): TeamReportingOption[] => {
  const seen = new Set<string>();

  return items.filter((item) => {
    const itemKey = String(item.id);
    if (seen.has(itemKey)) {
      return false;
    }

    seen.add(itemKey);
    return true;
  });
};

const toOptions = (
  items: unknown[],
  kind: "rating" | "role",
): TeamReportingOption[] => {
  const idKeys =
    kind === "rating"
      ? [
          "id",
          "rating",
          "ratingId",
          "rating_id",
          "likertScaleRatingId",
          "value",
        ]
      : ["id", "roleId", "role_id", "value"];
  const labelKeys =
    kind === "rating"
      ? ["label", "ratingText", "ratingLabel", "text", "name", "value"]
      : ["label", "roleText", "roleName", "text", "name", "value"];

  return uniqById(
    items.map((item, index) => {
      if (item === null || item === undefined) {
        const fallback = `${kind}-${index}`;
        return {
          id: fallback,
          label: fallback,
          value: fallback,
        };
      }

      if (!isRecord(item)) {
        const label = toDisplayString(item) || `${kind}-${index}`;
        return {
          id: label,
          label,
          value: label,
        };
      }

      const rawId = getFirstDefinedValue(item, idKeys);
      const optionId = isOptionId(rawId) ? rawId : `${kind}-${index}`;
      const baseLabel = toDisplayString(
        getFirstDefinedValue(item, labelKeys) ?? optionId,
      );
      const optionIdText = toDisplayString(optionId);
      const shouldPrefixRatingCode =
        kind === "rating" &&
        /^\d+$/.test(optionIdText) &&
        Boolean(baseLabel) &&
        baseLabel !== optionIdText;
      const label = shouldPrefixRatingCode
        ? `${optionIdText} - ${baseLabel}`
        : baseLabel;

      return {
        id: optionId,
        label,
        value: label,
      };
    }),
  );
};

const normalizePayload = (
  payload: unknown,
): TeamReportingRatingRolesPayload => {
  if (Array.isArray(payload)) {
    return {
      raw: payload,
      ratingOptions: toOptions(payload, "rating"),
      roleOptions: toOptions(payload, "role"),
    };
  }

  const payloadData = getNestedRecord(payload, "data");
  const ratings = [
    ...getArrayValue(payload, "ratings"),
    ...getArrayValue(payload, "ratingOptions"),
    ...getArrayValue(payload, "ratingList"),
    ...getArrayValue(payloadData, "ratings"),
    ...getArrayValue(payloadData, "ratingOptions"),
  ];
  const roles = [
    ...getArrayValue(payload, "roles"),
    ...getArrayValue(payload, "teamRoles"),
    ...getArrayValue(payload, "roleOptions"),
    ...getArrayValue(payload, "rolesOptions"),
    ...getArrayValue(payload, "roleList"),
    ...getArrayValue(payloadData, "roles"),
    ...getArrayValue(payloadData, "teamRoles"),
    ...getArrayValue(payloadData, "roleOptions"),
  ];

  return {
    raw: payload,
    ratingOptions: toOptions(ratings, "rating"),
    roleOptions: toOptions(roles, "role"),
  };
};

const normalizeToLeadsOptions = (payload: unknown): TeamReportingOption[] => {
  const payloadData = getNestedRecord(payload, "data");
  const list = Array.isArray(payload)
    ? payload
    : pickFirstArray([
        getArrayValue(payload, "privilegedMembers"),
        getArrayValue(payloadData, "privilegedMembers"),
        getArrayValue(payload, "items"),
        getArrayValue(payload, "data"),
        getArrayValue(payload, "leads"),
        getArrayValue(payload, "options"),
      ]);

  const seen = new Set<string>();

  return list
    .map((item, index) => {
      if (!isRecord(item)) {
        const fallback = toDisplayString(item) || `lead-${index}`;
        return {
          id: fallback,
          label: fallback,
          value: fallback,
        };
      }

      const composedName = [item.firstName, item.lastName, item.middleName]
        .map((part) => toDisplayString(part))
        .filter(Boolean)
        .join(" ");
      const rawId = getFirstDefinedValue(item, [
        "id",
        "leadId",
        "personId",
        "nuid",
        "value",
      ]);
      const optionId = isOptionId(rawId) ? rawId : `lead-${index}`;
      const label =
        composedName ||
        toDisplayString(
          getFirstDefinedValue(item, [
            "label",
            "name",
            "leadName",
            "fullName",
            "text",
            "value",
            "nuid",
          ]),
        ) ||
        `lead-${index}`;

      return {
        id: optionId,
        label,
        value: label,
      };
    })
    .filter((option) => {
      const optionKey = String(option.id);
      if (seen.has(optionKey)) {
        return false;
      }

      seen.add(optionKey);
      return true;
    });
};

const buildTeamsSearchParams = ({
  region,
  area,
  rating,
  roles,
  startDate,
  endDate,
  sortType,
  sortColumn,
  page,
  per_page,
  roleNuid,
}: {
  region?: TeamReportingSearchRequest["region"];
  area?: TeamReportingSearchRequest["area"];
  rating?: string;
  roles?: string;
  startDate?: string;
  endDate?: string;
  sortType?: TeamReportingSortDirection;
  sortColumn?: string;
  page: number;
  per_page: number;
  roleNuid?: string;
}): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    page,
    per_page,
  };

  if (isOptionId(region)) {
    params.region = region;
  }
  if (isOptionId(area)) {
    params.area = area;
  }
  if (rating) {
    params.rating = rating;
  }
  if (roles) {
    params.roles = roles;
  }
  if (startDate) {
    params.startDate = startDate;
  }
  if (endDate) {
    params.endDate = endDate;
  }
  if (sortType) {
    params.sortType = sortType;
  }
  if (sortColumn) {
    params.sortColumn = sortColumn;
  }
  if (roleNuid) {
    params.roleNuid = roleNuid;
  }

  return params;
};

const extractTeamsSearchRawItems = (
  payload: unknown,
): TeamReportingApiRow[] => {
  if (Array.isArray(payload)) {
    return payload as TeamReportingApiRow[];
  }
  if (isRecord(payload) && Array.isArray(payload.items)) {
    return payload.items as TeamReportingApiRow[];
  }
  if (isRecord(payload) && Array.isArray(payload.data)) {
    return payload.data as TeamReportingApiRow[];
  }

  return [];
};

const buildTeamsSearchMeta = ({
  payload,
  itemsLength,
  perPage,
  page,
}: {
  payload: unknown;
  itemsLength: number;
  perPage: number;
  page: number;
}): TeamReportingSearchMeta => {
  const payloadRecord = isRecord(payload) ? payload : undefined;
  const meta =
    getNestedRecord(payload, "meta") ?? getNestedRecord(payload, "pagination");

  const total = Number(
    meta?.total ??
      payloadRecord?.total ??
      payloadRecord?.totalItems ??
      payloadRecord?.count ??
      itemsLength,
  );
  const pageSize = Number(
    meta?.pageSize ??
      meta?.per_page ??
      payloadRecord?.pageSize ??
      payloadRecord?.per_page ??
      perPage,
  );
  const pageNumber = Number(
    meta?.pageNumber ??
      meta?.page ??
      payloadRecord?.pageNumber ??
      payloadRecord?.page ??
      page,
  );
  const totalPages = Number(
    meta?.totalPages ??
      payloadRecord?.totalPages ??
      (pageSize > 0 ? Math.ceil(total / pageSize) : 0),
  );

  return {
    total,
    totalPages,
    pageNumber,
    pageSize,
  };
};

/**
 * Loads team reporting rating and role filter options for the active user.
 */
export const fetchTeamsReportingRatingRoles = createAsyncThunk<
  TeamReportingRatingRolesPayload,
  FetchTeamsReportingRatingRolesArgs,
  { rejectValue: string }
>("teamsReport/fetchRatingRoles", async ({ nuid }, { rejectWithValue }) => {
  try {
    const response = await api.get(endpoints.ubt.teamsReport.getRatingRoles, {
      headers: {
        accept: "application/json",
        ...(nuid ? { "x-nuid": nuid } : {}),
      },
    });

    return normalizePayload(response?.data);
  } catch (error: unknown) {
    return rejectWithValue(
      extractErrorMessage(
        error,
        TEAM_REPORTING_ERROR_MESSAGES.fetchRatingRoles,
      ),
    );
  }
});

/**
 * Loads the available team reporting regions.
 */
export const fetchRegionOptions = createAsyncThunk<
  TeamReportingOption[],
  void,
  { rejectValue: string }
>("teamsReport/fetchRegionOptions", async (_, { rejectWithValue }) => {
  try {
    const response: APIResponse = await fetchLocations({
      loadValue: TEAM_REPORTING_REGION_LOAD_VALUE,
    });

    return transformToDropdownOptions(
      response?.selectedList ?? [],
      false,
    );
  } catch (error: unknown) {
    return rejectWithValue(
      extractErrorMessage(
        error,
        TEAM_REPORTING_ERROR_MESSAGES.fetchRegionOptions,
      ),
    );
  }
});

/**
 * Loads the available area options for a selected region.
 */
export const fetchAreaOptions = createAsyncThunk<
  TeamReportingOption[],
  FetchAreaOptionsArgs,
  { rejectValue: string }
>(
  "teamsReport/fetchAreaOptions",
  async (
    { regionCode, sortBy = TEAM_REPORTING_AREA_DEFAULT_SORT },
    { rejectWithValue },
  ) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: TEAM_REPORTING_AREA_LOAD_VALUE,
        regionCode,
        sortBy: sortBy === "name"
          ? TEAM_REPORTING_AREA_NAME_SORT
          : TEAM_REPORTING_AREA_DEFAULT_SORT,
      });

      return addDefaultOption(
        transformToDropdownOptions(response?.selectedList ?? []),
        TEAM_REPORTING_DEFAULT_OPTION,
      );
    } catch (error: unknown) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          TEAM_REPORTING_ERROR_MESSAGES.fetchAreaOptions,
        ),
      );
    }
  },
);

/**
 * Loads the lead member autocomplete options for the selected region and area.
 */
export const fetchTeamsReportingLeadsDropdown = createAsyncThunk<
  TeamReportingLeadsPayload,
  FetchTeamsReportingLeadsDropdownArgs,
  { rejectValue: string }
>(
  "teamsReport/fetchLeadsDropdown",
  async ({ regionCode, areaCode, nuid }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.ubt.teamsReport.getLeadsDropdown,
        {
          params: {
            regionCode,
            areaCode,
          },
          headers: {
            accept: "application/json",
            ...(nuid ? { "x-nuid": nuid } : {}),
          },
        },
      );

      return {
        raw: response?.data,
        options: normalizeToLeadsOptions(response?.data),
      };
    } catch (error: unknown) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          TEAM_REPORTING_ERROR_MESSAGES.fetchLeadsDropdown,
        ),
      );
    }
  },
);

/**
 * Executes the team reporting search with normalized filter and paging parameters.
 */
export const fetchTeamsReportingTeamsSearch = createAsyncThunk<
  TeamReportingSearchPayload,
  TeamReportingSearchRequest,
  { rejectValue: string }
>(
  "teamsReportfetchTeamsSearch",
  async (
    {
      region,
      area,
      rating,
      roles,
      startDate,
      endDate,
      sortType = TEAM_REPORTING_API_SORT_DIRECTION,
      sortColumn,
      page = TEAM_REPORTING_DEFAULT_PAGE,
      per_page = TEAM_REPORTING_DEFAULT_PAGE_SIZE,
      roleNuid,
      nuid,
    },
    { rejectWithValue },
  ) => {
    try {
      const params = buildTeamsSearchParams({
        region,
        area,
        rating,
        roles,
        startDate,
        endDate,
        sortType,
        sortColumn,
        page,
        per_page,
        roleNuid,
      });

      const response = await api.get(endpoints.ubt.teamsReport.teamsSearch, {
        params,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      });

      const items = normalizeTeamsSearchItems(
        extractTeamsSearchRawItems(response?.data),
      );

      return {
        raw: response?.data,
        items,
        meta: buildTeamsSearchMeta({
          payload: response?.data,
          itemsLength: items.length,
          perPage: per_page,
          page,
        }),
      };
    } catch (error: unknown) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          TEAM_REPORTING_ERROR_MESSAGES.fetchSearchResults,
        ),
      );
    }
  },
);

/**
 * Builds a query-parameter record shared by all report-download thunks.
 * Only fields with a defined, non-empty value are included so the backend
 * never receives empty-string parameters.
 */

/** Shared filter arguments used by report download thunks. */
type ReportFilterArgs = {
  region?: string;
  area?: string;
  rating?: string;
  roles?: string;
  startDate?: string;
  endDate?: string;
  roleNuid?: string;
  sortColumn?: string;
  sortType?: "ASC" | "DESC";
};

/** Filter arguments for P2P detail report download thunks (extends ReportFilterArgs with nuid). */
type P2PReportFilterArgs = ReportFilterArgs & { nuid?: string | null };

const buildReportFilterParams = ({
  region,
  area,
  rating,
  roles,
  startDate,
  endDate,
  roleNuid,
  sortColumn,
  sortType,
}: ReportFilterArgs): Record<string, string> => {
  const params: Record<string, string> = {};
  if (region) params.region = region;
  if (area) params.area = area;
  if (rating) params.rating = rating;
  if (roles) params.roles = roles;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (roleNuid) params.roleNuid = roleNuid;
  if (sortColumn) params.sortColumn = sortColumn;
  if (sortType) params.sortType = sortType;
  return params;
};

/**
 * Downloads the team reporting PDF report with the specified filters.
 * Fetches HTML content from API and uses PdfReportUtility to parse and display.
 * @param region - Region code filter (e.g., "02")
 * @param area - Area code filter (e.g., "10", "All")
 * @param rating - Comma-separated rating IDs (e.g., "3,4,5")
 * @param roles - Comma-separated role names
 * @param startDate - Start date in MM-DD-YYYY format
 * @param endDate - End date in MM-DD-YYYY format
 * @param roleNuid - Lead member name or user's NUID for authentication header
 */
export const downloadTeamsReportingPDF = createAsyncThunk<
  void,
  ReportFilterArgs,
  { rejectValue: string }
>(
  "teamsReport/downloadPDF",
  async (
    {
      region,
      area,
      rating,
      roles,
      startDate,
      endDate,
      roleNuid,
      sortColumn,
      sortType,
    },
    { rejectWithValue },
  ) => {
    try {
      const params = buildReportFilterParams({ region, area, rating, roles, startDate, endDate, roleNuid, sortColumn, sortType });

      // Fetch HTML content as blob (API returns JSON with htmlContent property)
      const response = await api.get(endpoints.ubt.teamsReport.downloadPDF, {
        params,
        headers: {
          accept: "application/json",
        },
        responseType: "blob",
      });

      // Use PdfReportUtility to parse JSON and trigger download
      await printHtmlReportToPdf(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          "Failed to download teams reporting PDF. Please try again.",
        ),
      );
    }
  },
);

/**
 * Downloads the team reporting summary as CSV with the specified filters.
 * Fetches CSV content from API and triggers a file download.
 * @param region - Region code filter (e.g., "02")
 * @param area - Area code filter (e.g., "10", "All")
 * @param rating - Comma-separated rating IDs (e.g., "3,4,5")
 * @param roles - Comma-separated role names
 * @param startDate - Start date in MM-DD-YYYY format
 * @param endDate - End date in MM-DD-YYYY format
 * @param roleNuid - Lead member name or user's NUID for authentication header
 * @param nuid - User's NUID for authentication header (required)
 */
export const downloadTeamsReportingCSV = createAsyncThunk<
  void,
  ReportFilterArgs,
  { rejectValue: string }
>(
  "teamsReport/downloadCSV",
  async (
    {
      region,
      area,
      rating,
      roles,
      startDate,
      endDate,
      roleNuid,
      sortColumn,
      sortType,
    },
    { rejectWithValue },
  ) => {
    try {
      const params = buildReportFilterParams({ region, area, rating, roles, startDate, endDate, roleNuid, sortColumn, sortType });

      // Fetch CSV content as blob
      const response = await api.get(
        endpoints.ubt.teamsReport.downloadTeamsReportingCSV,
        {
          params,
          headers: {
            accept: "application/json",
          },
          responseType: "blob",
        },
      );

      // Trigger CSV file download using shared utility
      const fileName = resolveCsvFileName(
        response.headers["content-disposition"] as string | undefined,
        "TEAMS-REPORTING.csv",
      );
      downloadCsvFile(response.data as Blob, fileName);
    } catch (error: unknown) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          "Failed to download teams reporting CSV. Please try again.",
        ),
      );
    }
  },
);

/**
 * Downloads the UBT Details report by teams as CSV.
 * Sends current teams search filters to the API and triggers a browser download.
 * @param region - Region code filter
 * @param area - Area code filter
 * @param rating - Comma-separated rating IDs
 * @param roles - Comma-separated role names
 * @param startDate - Start date in MM-DD-YYYY format
 * @param endDate - End date in MM-DD-YYYY format
 * @param roleNuid - Lead member name or user's NUID
 */
export const exportUBTDetailsByTeamsCSV = createAsyncThunk<
  void,
  {
    region?: string;
    area?: string;
    rating?: string;
    roles?: string;
    startDate?: string;
    endDate?: string;
    roleNuid?: string;
    sortColumn?: string;
    sortOrder?: "ASC" | "DESC";
  },
  { rejectValue: string }
>(
  "teamsReport/exportUBTDetailsByTeamsCSV",
  async (
    {
      region,
      area,
      rating,
      roles,
      startDate,
      endDate,
      roleNuid,
      sortColumn,
      sortOrder,
    },
    { rejectWithValue },
  ) => {
    try {
      const params: Record<string, string> = {};
      if (region) params.region = region;
      if (area) params.area = area;
      if (rating) params.rating = rating;
      if (roles) params.roles = roles;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (roleNuid) params.roleNuid = roleNuid;
      if (sortColumn) params.sortColumn = sortColumn;
      if (sortOrder) params.sortOrder = sortOrder;

      const response = await api.get(
        endpoints.ubt.reports.exportDetailsByTeams,
        {
          params,
          headers: { accept: "text/csv" },
          responseType: "blob",
        },
      );

      const fileName = resolveCsvFileName(
        response.headers["content-disposition"] as string | undefined,
        "UBT_DETAILS.csv",
      );
      downloadCsvFile(response.data as Blob, fileName);
    } catch (error: unknown) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          "Failed to export UBT Details by Teams CSV. Please try again.",
        ),
      );
    }
  },
);

/**
 * Downloads the UBT membership count report as CSV.
 * Accepts current table sort column/direction and all active search filters.
 *
 * @param sortBy - Current table sort column (dynamic, e.g. "teamid")
 * @param sortOrder - Current table sort direction (dynamic, e.g. "DESC")
 * @param regionCode - Region code filter
 * @param entityCode - Entity code filter
 * @param areaCode - Area code filter
 * @param serviceAreaCode - Service area code filter
 * @param locationCode - Location code filter
 * @param costCenter - Cost center filter
 * @param searchString - Free-text search filter
 * @param nuid - User NUID for the x-nuid header
 */
export const downloadMembershipCountCSV = createAsyncThunk<
  void,
  {
    sortBy?: string;
    sortOrder?: string;
    regionCode?: string;
    entityCode?: string;
    areaCode?: string;
    serviceAreaCode?: string;
    locationCode?: string;
    costCenter?: string;
    searchString?: string;
    nuid?: string | null;
  },
  { rejectValue: string }
>(
  "teamsReport/downloadMembershipCountCSV",
  async (
    {
      sortBy,
      sortOrder,
      regionCode,
      entityCode,
      areaCode,
      serviceAreaCode,
      locationCode,
      costCenter,
      searchString,
      nuid,
    },
    { rejectWithValue },
  ) => {
    try {
      const params: Record<string, string> = {};
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;
      if (regionCode) params.regionCode = regionCode;
      if (entityCode) params.entityCode = entityCode;
      if (areaCode) params.areaCode = areaCode;
      if (serviceAreaCode) params.serviceAreaCode = serviceAreaCode;
      if (locationCode) params.locationCode = locationCode;
      if (costCenter) params.costCenter = costCenter;
      if (searchString) params.searchString = searchString;

      const response = await api.get(
        endpoints.ubt.teamsReport.membershipCountCSV,
        {
          params,
          headers: {
            accept: "text/csv",
            ...(nuid ? { "x-nuid": nuid } : {}),
          },
          responseType: "blob",
        },
      );

      const fileName = resolveCsvFileName(
        response.headers["content-disposition"] as string | undefined,
        "UBT-MEMBERSHIP-COUNT.csv",
      );
      downloadCsvFile(response.data as Blob, fileName);
    } catch (error: unknown) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          "Failed to download membership count CSV. Please try again.",
        ),
      );
    }
  },
);

/**
 * Condition guard shared by both P2P download thunks.
 * Returns false (preventing dispatch) when a PDF or CSV P2P download is
 * already in flight, avoiding duplicate backend database connections.
 */
const isP2PDownloadIdle = (
  _: unknown,
  { getState }: { getState: () => TeamsReportSelectorState },
): boolean => {
  const slice = getState().teamsReport;
  return !((slice?.pdfDownloadLoading ?? false) || (slice?.csvDownloadLoading ?? false));
};

/**
 * Downloads the P2P detail reporting PDF with the current search filter params.
 * A condition guard prevents duplicate in-flight requests: the thunk will not
 * start if a PDF or CSV P2P download is already in progress.
 * @param region - Region code filter
 * @param area - Area code filter
 * @param rating - Comma-separated rating IDs
 * @param roles - Comma-separated role names
 * @param startDate - Start date in MM-DD-YYYY format
 * @param endDate - End date in MM-DD-YYYY format
 * @param roleNuid - Lead member NUID (sent only when "Search all lead roles" is active and a lead is selected)
 * @param sortColumn - Sort column (valid value: 'teamId')
 * @param sortType - Sort direction ('ASC' | 'DESC')
 * @param nuid - Authenticated user NUID sent as x-nuid header (required by the API)
 */
export const downloadP2PDetailReportingPDF = createAsyncThunk<
  void,
  P2PReportFilterArgs,
  { rejectValue: string; state: TeamsReportSelectorState }
>(
  "teamsReport/downloadP2PDetailPDF",
  async (
    { region, area, rating, roles, startDate, endDate, roleNuid, sortColumn, sortType, nuid },
    { rejectWithValue, signal },
  ) => {
    try {
      const params = buildReportFilterParams({ region, area, rating, roles, startDate, endDate, roleNuid, sortColumn, sortType });

      // Fetch HTML content as blob (API returns JSON with htmlContent property)
      const response = await api.get(
        endpoints.ubt.teamsReport.downloadP2PDetailPDF,
        {
          params,
          headers: {
            accept: "application/json",
            ...(nuid ? { "x-nuid": nuid } : {}),
          },
          responseType: "blob",
          signal,
        },
      );

      // Build the custom filename: P2P_DETAILS_REPORT_{pstDateTimeSuffix}
      const pdfFileName = `P2P_DETAILS_REPORT${getPstDateTimeSuffix()}`;

      // Parse the blob to extract the HTML string (mirrors PdfReportUtility).
      const responseText = await (response.data as Blob).text();
      let htmlString: string;
      try {
        const parsed = JSON.parse(responseText) as { htmlContent?: string };
        htmlString = parsed.htmlContent ?? responseText;
      } catch {
        htmlString = responseText;
      }

      if (!htmlString || htmlString.trim().length === 0) {
        throw new Error("HTML content is empty or contains only whitespace");
      }

      // Strip any <title> tag from the backend HTML so the injected script's
      // document.title assignment is the sole source of the suggested filename.
      htmlString = htmlString.replace(/<title[^>]*>.*?<\/title>/, "");

      // Suppress the browser's native print headers/footers (title, URL,
      // date, page number) by overriding the @page top/bottom margins to 0.
      // Chrome renders those decorations in the margin area — zero margin
      // leaves no space for them so Chrome omits them entirely.
      // The existing backend @page rule sets margin: 10mm; this later rule
      // overrides only top/bottom, keeping the left/right spacing intact.
      const suppressHeadersStyle =
        "<style>@page{margin-top:2mm;margin-bottom:2mm}</style>";
      htmlString = /<\/head>/i.test(htmlString)
        ? htmlString.replace(/<\/head>/i, `${suppressHeadersStyle}</head>`)
        : `${suppressHeadersStyle}${htmlString}`;

      // Set document.title in the child tab's injected script so the browser's
      // "Save as PDF" dialog suggests pdfFileName. This tab is opened with
      // noopener,noreferrer, so it runs in an isolated process — the parent
      // app's document.title is never affected.
      const scriptBody =
        "window.addEventListener('load',function(){" +
        `document.title=${JSON.stringify(pdfFileName)};` +
        "window.print();" +
        "});" +
        "window.addEventListener('afterprint',function(){window.close();});";
      const autoPrintScript = `<script>${scriptBody}</` + `script>`;

      const printReadyHtml = /<\/body>/i.test(htmlString)
        ? htmlString.replace(/<\/body>/i, `${autoPrintScript}</body>`)
        : `${htmlString}${autoPrintScript}`;

      const htmlBlob = new Blob([printReadyHtml], { type: "text/html" });
      const blobUrl = globalThis.URL.createObjectURL(htmlBlob);
      // See printHtmlReportToPdf: noopener,noreferrer puts the tab in a
      // separate process so the print dialog does not freeze the parent tab.
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (error: unknown) {
      if (signal.aborted) return;
      return rejectWithValue(
        await extractBlobErrorMessage(
          error,
          "Failed to download P2P detail reporting PDF. Please try again.",
        ),
      );
    }
  },
  {
    /**
     * Prevent duplicate in-flight PDF/CSV P2P requests.
     * If either download is already running, this thunk is cancelled before
     * it can open a new backend database connection.
     */
    condition: isP2PDownloadIdle,
  },
);

/**
 * Downloads the P2P detail reporting CSV with the current search filter params.
 * A condition guard prevents duplicate in-flight requests: the thunk will not
 * start if a PDF or CSV P2P download is already in progress.
 * @param region - Region code filter
 * @param area - Area code filter
 * @param rating - Comma-separated rating IDs
 * @param roles - Comma-separated role names
 * @param startDate - Start date in MM-DD-YYYY format
 * @param endDate - End date in MM-DD-YYYY format
 * @param roleNuid - Lead member NUID (sent only when "Search all lead roles" is active and a lead is selected)
 * @param sortColumn - Sort column (valid value: 'teamId')
 * @param sortType - Sort direction ('ASC' | 'DESC')
 * @param nuid - Authenticated user NUID sent as x-nuid header (required by the API)
 */
export const downloadP2PDetailReportingCSV = createAsyncThunk<
  void,
  P2PReportFilterArgs,
  { rejectValue: string; state: TeamsReportSelectorState }
>(
  "teamsReport/downloadP2PDetailCSV",
  async (
    { region, area, rating, roles, startDate, endDate, roleNuid, sortColumn, sortType, nuid },
    { rejectWithValue, signal },
  ) => {
    try {
      const params = buildReportFilterParams({ region, area, rating, roles, startDate, endDate, roleNuid, sortColumn, sortType });

      // Fetch CSV content as blob
      const response = await api.get(
        endpoints.ubt.teamsReport.downloadP2PDetailCSV,
        {
          params,
          headers: {
            accept: "application/json",
            ...(nuid ? { "x-nuid": nuid } : {}),
          },
          responseType: "blob",
          signal,
        },
      );

      // Trigger CSV file download using shared utility
      const fileName = resolveCsvFileName(
        response.headers["content-disposition"] as string | undefined,
        "P2P-DETAIL-REPORTING.csv",
      );
      downloadCsvFile(response.data as Blob, fileName);
    } catch (error: unknown) {
      if (signal.aborted) return;
      return rejectWithValue(
        await extractBlobErrorMessage(
          error,
          "Failed to download P2P detail reporting CSV. Please try again.",
        ),
      );
    }
  },
  {
    /**
     * Prevent duplicate in-flight PDF/CSV P2P requests.
     * If either download is already running, this thunk is cancelled before
     * it can open a new backend database connection.
     */
    condition: isP2PDownloadIdle,
  },
);

export const downloadDisplaySummaryAndDetailsReport = createAsyncThunk<
  void,
  Record<string, string>,
  { rejectValue: string }
>(
  "teamsReport/downloadDisplaySummaryAndDetails",
  async (params, { rejectWithValue }) => {
    try {
      // Fetch HTML content as blob (API returns JSON with htmlContent property)
      const response = await api.get(
        endpoints.ubt.reports.displaySummaryAndDetails,
        {
          params,
          headers: {
            accept: "application/json",
          },
          responseType: "blob",
        },
      );

      // Use PdfReportUtility to parse JSON and trigger download
      await printHtmlReportToPdf(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          "Failed to download display summary and details report. Please try again.",
        ),
      );
    }
  },
);

const teamsReportSlice = createSlice({
  name: "teamsReportSlice",
  initialState,
  reducers: {
    /**
     * Clears the active team reporting filters, results, and request state.
     */
    clearTeamsReportingRatingRolesState(state) {
      state.regionOptions = [];
      state.areaOptions = [];
      state.ratingOptions = [];
      state.roleOptions = [];
      state.leadsDropdownOptions = [];
      state.teamsSearchResults = [];
      state.teamsSearchMeta = { ...TEAM_REPORTING_EMPTY_META };
      state.raw = null;
      state.loading = false;
      state.error = null;
      state.leadsLoading = false;
      state.leadsError = null;
      state.loadingRegion = false;
      state.loadingArea = false;
      state.teamsSearchLoading = false;
      state.teamsSearchError = null;
      state.pdfDownloadLoading = false;
      state.pdfDownloadError = null;
    },
    /**
     * Clears dependent dropdown values when an upstream selection changes.
     */
    resetDependentDropdowns(
      state,
      action: PayloadAction<ResetDependentDropdownsPayload>,
    ) {
      if (action.payload.level === "region") {
        state.areaOptions = [];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRegionOptions.pending, (state) => {
        state.loadingRegion = true;
      })
      .addCase(fetchRegionOptions.fulfilled, (state, action) => {
        state.loadingRegion = false;
        state.regionOptions = action.payload;
      })
      .addCase(fetchRegionOptions.rejected, (state) => {
        state.loadingRegion = false;
      })
      .addCase(fetchAreaOptions.pending, (state) => {
        state.loadingArea = true;
      })
      .addCase(fetchAreaOptions.fulfilled, (state, action) => {
        state.loadingArea = false;
        state.areaOptions = action.payload;
      })
      .addCase(fetchAreaOptions.rejected, (state) => {
        state.loadingArea = false;
      })
      .addCase(fetchTeamsReportingRatingRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeamsReportingRatingRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.raw = action.payload.raw;
        state.ratingOptions = action.payload.ratingOptions;
        state.roleOptions = action.payload.roleOptions;
      })
      .addCase(fetchTeamsReportingRatingRoles.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ??
          action.error.message ??
          TEAM_REPORTING_ERROR_MESSAGES.fetchRatingRoles;
      })
      .addCase(fetchTeamsReportingLeadsDropdown.pending, (state) => {
        state.leadsLoading = true;
        state.leadsError = null;
      })
      .addCase(fetchTeamsReportingLeadsDropdown.fulfilled, (state, action) => {
        state.leadsLoading = false;
        state.leadsError = null;
        state.raw = action.payload.raw;
        state.leadsDropdownOptions = action.payload.options;
      })
      .addCase(fetchTeamsReportingLeadsDropdown.rejected, (state, action) => {
        state.leadsLoading = false;
        state.leadsError =
          action.payload ??
          action.error.message ??
          TEAM_REPORTING_ERROR_MESSAGES.fetchLeadsDropdown;
      })
      .addCase(fetchTeamsReportingTeamsSearch.pending, (state) => {
        state.teamsSearchLoading = true;
        state.teamsSearchError = null;
      })
      .addCase(fetchTeamsReportingTeamsSearch.fulfilled, (state, action) => {
        state.teamsSearchLoading = false;
        state.teamsSearchError = null;
        state.raw = action.payload.raw;
        state.teamsSearchResults = action.payload.items;
        state.teamsSearchMeta = action.payload.meta;
      })
      .addCase(fetchTeamsReportingTeamsSearch.rejected, (state, action) => {
        state.teamsSearchLoading = false;
        state.teamsSearchError =
          action.payload ??
          action.error.message ??
          TEAM_REPORTING_ERROR_MESSAGES.fetchSearchResults;
      })
      .addCase(downloadDisplaySummaryAndDetailsReport.pending, (state) => {
        state.pdfDownloadLoading = true;
        state.pdfDownloadError = null;
      })
      .addCase(downloadDisplaySummaryAndDetailsReport.fulfilled, (state) => {
        state.pdfDownloadLoading = false;
        state.pdfDownloadError = null;
      })
      .addCase(
        downloadDisplaySummaryAndDetailsReport.rejected,
        (state, action) => {
          state.pdfDownloadLoading = false;
          state.pdfDownloadError =
            action.payload ??
            action.error.message ??
            "Failed to download report";
        },
      )
      .addCase(downloadTeamsReportingPDF.pending, (state) => {
        state.pdfDownloadLoading = true;
        state.pdfDownloadError = null;
      })
      .addCase(downloadTeamsReportingPDF.fulfilled, (state) => {
        state.pdfDownloadLoading = false;
        state.pdfDownloadError = null;
      })
      .addCase(downloadTeamsReportingPDF.rejected, (state, action) => {
        state.pdfDownloadLoading = false;
        state.pdfDownloadError =
          action.payload ?? action.error.message ?? "Failed to download PDF";
      })
      .addCase(downloadTeamsReportingCSV.pending, (state) => {
        state.csvDownloadLoading = true;
        state.csvDownloadError = null;
      })
      .addCase(downloadTeamsReportingCSV.fulfilled, (state) => {
        state.csvDownloadLoading = false;
        state.csvDownloadError = null;
      })
      .addCase(downloadTeamsReportingCSV.rejected, (state, action) => {
        state.csvDownloadLoading = false;
        state.csvDownloadError =
          action.payload ?? action.error.message ?? "Failed to download CSV";
      })
      .addCase(exportUBTDetailsByTeamsCSV.pending, (state) => {
        state.exportUBTDetailsLoading = true;
        state.exportUBTDetailsError = null;
      })
      .addCase(exportUBTDetailsByTeamsCSV.fulfilled, (state) => {
        state.exportUBTDetailsLoading = false;
        state.exportUBTDetailsError = null;
      })
      .addCase(exportUBTDetailsByTeamsCSV.rejected, (state, action) => {
        state.exportUBTDetailsLoading = false;
        state.exportUBTDetailsError =
          action.payload ??
          action.error.message ??
          "Failed to export UBT Details CSV";
      })
      .addCase(downloadP2PDetailReportingPDF.pending, (state) => {
        state.pdfDownloadLoading = true;
        state.pdfDownloadError = null;
      })
      .addCase(downloadP2PDetailReportingPDF.fulfilled, (state) => {
        state.pdfDownloadLoading = false;
        state.pdfDownloadError = null;
      })
      .addCase(downloadP2PDetailReportingPDF.rejected, (state, action) => {
        state.pdfDownloadLoading = false;
        state.pdfDownloadError =
          action.payload ?? action.error.message ?? "Failed to download PDF";
      })
      .addCase(downloadP2PDetailReportingCSV.pending, (state) => {
        state.csvDownloadLoading = true;
        state.csvDownloadError = null;
      })
      .addCase(downloadP2PDetailReportingCSV.fulfilled, (state) => {
        state.csvDownloadLoading = false;
        state.csvDownloadError = null;
      })
      .addCase(downloadP2PDetailReportingCSV.rejected, (state, action) => {
        state.csvDownloadLoading = false;
        state.csvDownloadError =
          action.payload ??
          action.error.message ??
          "Failed to download P2P CSV";
      })
      .addCase(downloadMembershipCountCSV.pending, (state) => {
        state.membershipCountLoading = true;
        state.membershipCountError = null;
      })
      .addCase(downloadMembershipCountCSV.fulfilled, (state) => {
        state.membershipCountLoading = false;
        state.membershipCountError = null;
      })
      .addCase(downloadMembershipCountCSV.rejected, (state, action) => {
        state.membershipCountLoading = false;
        state.membershipCountError =
          action.payload ??
          action.error.message ??
          "Failed to download membership count CSV";
      });
  },
});

export const { clearTeamsReportingRatingRolesState, resetDependentDropdowns } =
  teamsReportSlice.actions;

const selectTeamsReportingSliceState = (
  state: TeamsReportSelectorState,
): TeamsReportState =>
  state.teamsReport ?? state.teamsReportingRatingRoles ?? initialState;

export const selectTeamsReportingRegionOptions = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.regionOptions,
);

export const selectTeamsReportingAreaOptions = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.areaOptions,
);

export const selectTeamsReportingRegionLoading = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.loadingRegion,
);

export const selectTeamsReportingAreaLoading = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.loadingArea,
);

export const selectTeamsReportingRatingOptions = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.ratingOptions,
);

export const selectTeamsReportingRoleOptions = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.roleOptions,
);

export const selectTeamsReportingRatingRolesLoading = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.loading,
);

export const selectTeamsReportingRatingRolesError = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.error,
);

export const selectTeamsReportingLeadsDropdownOptions = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.leadsDropdownOptions,
);

export const selectTeamsReportingLeadsDropdownLoading = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.leadsLoading,
);

export const selectTeamsReportingLeadsDropdownError = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.leadsError,
);

export const selectTeamsReportingSearchResults = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.teamsSearchResults,
);

export const selectTeamsReportingSearchMeta = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.teamsSearchMeta,
);

export const selectTeamsReportingSearchLoading = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.teamsSearchLoading,
);

export const selectTeamsReportingSearchError = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.teamsSearchError,
);

export const selectTeamsReportingTableRows = createSelector(
  [selectTeamsReportingSearchResults],
  (results): TeamReportingTableRow[] =>
    results.map((result) => ({
      ...result,
      rowKey: `team-${String(result.ubtId ?? result.teamId)}-0`,
      id: result.ubtId ?? result.teamId,
    })),
);

export const selectTeamsReportingPageLoading = createSelector(
  [
    selectTeamsReportingLeadsDropdownLoading,
    selectTeamsReportingRegionLoading,
    selectTeamsReportingAreaLoading,
    selectTeamsReportingSearchLoading,
  ],
  (leadsLoading, regionLoading, areaLoading, searchLoading) =>
    leadsLoading || regionLoading || areaLoading || searchLoading,
);

export const selectIsPdfDownloadLoading = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.pdfDownloadLoading,
);

export const isPdfDownloadLoading = selectIsPdfDownloadLoading;

export const selectTeamsReportingPDFDownloadError = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.pdfDownloadError,
);

export const selectTeamsReportingCSVDownloadLoading = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.csvDownloadLoading,
);

export const selectTeamsReportingCSVDownloadError = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.csvDownloadError,
);

export const selectTeamsReportingExportUBTDetailsLoading = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.exportUBTDetailsLoading,
);

export const selectTeamsReportingExportUBTDetailsError = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.exportUBTDetailsError,
);

/**
 * Selects whether the membership count CSV download is in progress.
 */
export const selectMembershipCountLoading = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.membershipCountLoading,
);

/**
 * Selects the membership count CSV download error message, or null if none.
 */
export const selectMembershipCountError = createSelector(
  [selectTeamsReportingSliceState],
  (teamsReportState) => teamsReportState.membershipCountError,
);

export default teamsReportSlice.reducer;
